// 📁 frontend/src/sezioni/MessaggiStorico.js
// Entry point della sezione Messaggi. Si apre dal menu sulla lista thread.
// Gestisce internamente la navigazione verso dettaglio e nuova comunicazione.
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import MessaggiDettaglio from "./MessaggiDettaglio";
import MessaggiNuovo from "./MessaggiNuovo";

const POLL_LIST_MS = 30000;

function buildHeaders(user) {
  const h = {
    "X-Session-Token": user.token,
    "X-User-Id": String(user.id),
  };
  if (user.assunzione_id) h["X-Assunzione-Id"] = String(user.assunzione_id);
  if (user.azienda_id)    h["X-Azienda-Id"]    = String(user.azienda_id);
  return h;
}

function shortTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
}

const STATUS = {
  PENDING:  { label: "In attesa" },
  ANSWERED: { label: "Risposto"  },
  CLOSED:   { label: "Chiuso"    },
};

const IconBack = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);
const IconPlus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
  </svg>
);

function ListaThread({ threads, loading, onOpen }) {
  const statoColor = (status) => {
    switch (status) {
      case "PENDING":  return "bg-yellow-300";
      case "ANSWERED": return "bg-green-500";
      case "CLOSED":   return "bg-gray-400";
      default:         return "bg-gray-300";
    }
  };
  const statoLabel = (status) => (STATUS[status] || STATUS.PENDING).label;

  return (
    <div style={{ height: "calc(100dvh - 3.5rem - env(safe-area-inset-top))", overflowY: "auto", paddingBottom: "env(safe-area-inset-bottom)" }}>
    <div className="bg-white rounded-xl shadow-lg p-4 max-w-2xl mx-auto mt-4 w-full">
      {loading ? (
        <div>Caricamento...</div>
      ) : threads.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Nessuna comunicazione. Tocca <b>+</b> in alto a destra per scriverne una nuova.
        </div>
      ) : (
        <table className="w-full text-base">
          <thead>
            <tr className="border-b">
              <th className="font-medium text-primary text-sm sm:text-base font-bold text-left">Oggetto</th>
              <th className="font-medium text-primary text-sm sm:text-base font-bold text-center">Data</th>
              <th className="font-medium text-primary text-sm sm:text-base font-bold text-center">Stato</th>
            </tr>
          </thead>
          <tbody>
            {threads.map((t) => {
              const title = t.created_by === "HR"
                ? (t.oggetto || "Comunicazione")
                : (t.categoria_nome || "Comunicazione");
              return (
                <React.Fragment key={t.id}>
                  <tr
                    className="cursor-pointer active:bg-blue-50 transition"
                    onClick={() => onOpen(t.id)}
                    title="Apri conversazione"
                  >
                    <td className="pt-2 pb-0 text-left">
                      <div className="flex items-center gap-2">
                        {t.unread_employee > 0 && (
                          <span className="inline-block w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                        )}
                        <span className="font-semibold truncate">{title}</span>
                        {t.unread_employee > 0 && (
                          <span className="text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5 flex-shrink-0">
                            {t.unread_employee}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="pt-2 pb-0 text-center text-sm text-gray-600">{shortTime(t.data_ultimo_msg)}</td>
                    <td className="pt-2 pb-0 text-center align-middle">
                      <span
                        className={"inline-block w-4 h-4 rounded-full mt-2 " + statoColor(t.status)}
                        title={statoLabel(t.status)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="py-0 px-4 text-gray-500 text-left text-xs">
                      {t.ultimo_interlocutore === "HR"
                        ? "Risposta HR"
                        : t.ultimo_interlocutore === "SYSTEM"
                        ? "Sistema"
                        : "Inviata da te"}
                    </td>
                  </tr>
                  <tr><td colSpan={3} className="border-b p-0"></td></tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
    </div>
  );
}

export default function MessaggiStorico({ user, isOnline, initialThreadId, onSetTopBarAction }) {
  const [view, setView] = useState(initialThreadId ? "dettaglio" : "lista");
  const [threadId, setThreadId] = useState(initialThreadId || null);
  const [threads, setThreads] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unreadTotal = useMemo(() => threads.reduce((s, t) => s + Number(t.unread_employee || 0), 0), [threads]);

  // Sopprime warning lint — unreadTotal è disponibile per usi futuri (es. badge drawer)
  void unreadTotal;

  useEffect(() => {
    if (!onSetTopBarAction) return;
    if (view === "lista") {
      onSetTopBarAction({ icon: <IconPlus />, onClick: () => setView("nuovo"), label: "Nuova comunicazione" });
    } else if (view === "dettaglio") {
      onSetTopBarAction({ icon: <IconBack />, onClick: () => { setView("lista"); loadList(); }, label: "Indietro" });
    } else if (view === "nuovo") {
      onSetTopBarAction({ icon: <IconBack />, onClick: () => setView("lista"), label: "Indietro" });
    }
    return () => onSetTopBarAction(null);
    // eslint-disable-next-line
  }, [view, onSetTopBarAction]);

  async function loadList() {
    try {
      const r = await api.get("/api/chat/threads", { headers: buildHeaders(user) });
      setThreads(r.threads || []);
    } catch (err) {
      setError(err.message || "Errore lista");
    } finally {
      setLoading(false);
    }
  }

  async function loadCategorie() {
    try {
      const r = await api.get("/api/chat/categorie", { headers: buildHeaders(user) });
      setCategorie(r.categorie || []);
    } catch (_) { }
  }

  useEffect(() => {
    if (!user?.id || !user?.token) return;
    setError(null); setLoading(true);
    loadList(); loadCategorie();
    const iv = setInterval(loadList, POLL_LIST_MS);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [user?.id, user?.token]);

  if (!isOnline) {
    return <div className="p-6 text-center text-gray-500">Comunicazioni disponibili solo online.</div>;
  }
  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 font-semibold mb-2">Errore</div>
        <div className="text-sm text-gray-600">{error}</div>
      </div>
    );
  }

  if (view === "nuovo") {
    return (
      <MessaggiNuovo
        user={user}
        categorie={categorie}
        onCreated={(id) => { setThreadId(id); setView("dettaglio"); loadList(); }}
        onCancel={() => setView("lista")}
      />
    );
  }
  if (view === "dettaglio" && threadId) {
    return (
      <MessaggiDettaglio
        user={user}
        threadId={threadId}
      />
    );
  }
  return (
    <ListaThread
      threads={threads}
      loading={loading}
      onOpen={(id) => { setThreadId(id); setView("dettaglio"); }}
    />
  );
}
